import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnapshot } from "valtio";
import { useSelector } from "react-redux";

import {
    store as associateStore,
    setIsAddPeripheralClicked,
    setMonetics,
} from "./store";
import { store as userStore } from "@components/VerticalLayout/store";
import {
    setActions,
    setIsActionsClicked,
    setValidationAction,
    store as moneticsStore,
    setIsEdited,
} from "@pages/Peripherals/Monetics/store";

import { useCallbackPrompt } from "@hooks/useCallbackPrompt";
import { UpdateResourceIp } from "@helpers/general";
import { getAuthorizationStates } from "@components/VerticalLayout/StyledSidebarMenu";
import { useAuthHeader } from "../../../api/headersKeycloak";
import { useFetchGlobal } from "../../../api/hooks";

import MoneticsActionColumn from "./MoneticsActionColumn";
import DropDownAction from "./DropDownAction";
import { ID_MONETICS } from "@constants/menu";
import { PERIPHERAL_TYPE_MONETIC } from "@constants/index";

type SubRow = {
    peripheralType: string;
    peripheralId: string;
    id_monetic?: string;
    shopId?: string;
    [key: string]: unknown;
};

type ApplicationRow = {
    shopId: string;
    subRows?: SubRow[];
    appId?: string;
    [key: string]: unknown;
};

type PeripheralElement = {
    Id: string;
    [key: string]: unknown;
};

type ColumnRenderProps = {
    selectedRow: Record<string, unknown> & { original: Record<string, unknown> };
};

type RootState = {
    global: {
        infoUser: {
            profileAuthorization: Record<string, unknown>;
        };
    };
};

type UseMoneticsProp = {
    rowData: ApplicationRow;
    selectedRows: ApplicationRow[];
};

export function useMonetics({ rowData, selectedRows }: UseMoneticsProp) {
    const { t } = useTranslation();
    const getAuthHeader = useAuthHeader();
    const { fetchGlobal } = useFetchGlobal();
    const moneticsInitialized = React.useRef(false);

    const { isAddPeripheralClicked } = useSnapshot(associateStore);
    const { oneShop, userID, franchiseID, shopID } = useSnapshot(userStore);
    const {
        isActionsClicked,
        actions,
        validationAction,
        isEdited,
    } = useSnapshot(moneticsStore);
    const authorizationList = useSelector(
        (state: RootState) => state.global.infoUser.profileAuthorization
    );
    const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(
        isEdited
    );
    const validSelectedRows = React.useMemo(
        () => selectedRows.filter(
            (elt: ApplicationRow) => elt[t("Application")] !== ""
        ),
        [selectedRows, t]
    );
    const shopId = oneShop
        ? shopID
        : validSelectedRows.length > 0
        ? validSelectedRows[0].shopId
        : rowData.shopId;
    const modify = getAuthorizationStates(ID_MONETICS, authorizationList)
        .Modifie;

    const [
        isAddNewMoneticsButtonClicked,
        setIsAddNewMoneticsButtonClicked,
    ] = useState(false);
    const [isModalOpened, setIsModalOpened] = useState(false);
    const [data, setData] = useState<Record<string, unknown>>({});
    const [
        isConfirmationEditRunning,
        setIsConfirmationEditRunning,
    ] = React.useState<boolean>(false);
    const [
        isInInitializationModalOpened,
        setIsInInitializationModalOpened,
    ] = React.useState<boolean>(false);
    const i18nextLng = localStorage.getItem("i18nextLng") || "fr";
    const [editedData, setEditedData] = React.useState<PeripheralElement[]>([]);
    const [moneticId] = React.useState<string>("");
    const [dataMoneticIsUpdated, setDataMoneticIsUpdated] = React.useState<
        boolean | number
    >(false);
    const [filterActive, setLocalFilterActive] = React.useState<boolean>(false);
    const [
        isModalDeleteAssociatePeripheralOpened,
        setIsModalDeleteAssociatePeripheralOpened,
    ] = React.useState<boolean>(false);
    const [selectMark, setSelectMark] = React.useState<boolean>(false);
    const [inputDesignation, setInputDesignation] = React.useState<boolean>(
        false
    );
    const [InputIp, setInputIp] = React.useState<boolean>(false);
    const [ipExist, setIpExist] = React.useState<boolean>(false);
    const [, setIsUpdateOperation] = React.useState<boolean>(false);

    function customJsxComponent(e: ColumnRenderProps) {
        return (
            (e.selectedRow.original[t("Application")] !== "" ||
                e.selectedRow.original[t("Peripheral")] !== "") && (
                <div className="w-100 d-flex justify-content-center">
                    <DropDownAction
                        element={e}
                        setEditedData={setEditedData}
                        setInputIp={setInputIp}
                    />
                </div>
            )
        );
    }

    const arrayOfCustomColumns = React.useMemo(() => [
        {
            indexOFColumn: 999,
            columnName: t("Actions"),
            customJsx: (e: ColumnRenderProps) => (
                <MoneticsActionColumn
                    rowData={rowData}
                    element={e.selectedRow as unknown as PeripheralElement}
                    selectedRows={selectedRows}
                />
            ),
        },
        {
            indexOFColumn: 0,
            columnName: t("Designation"),
            customJsx: (e: ColumnRenderProps) => {
                return <div>{e.selectedRow[t("Designation")] as string}</div>;
            },
        },
    ], [t, rowData, selectedRows]);

    const getArrayOfApplications = (): string[] => {
        const applications: string[] = [];
        selectedRows.forEach((elt: ApplicationRow) => {
            if (elt[t("Application")] !== "")
                applications.push(elt[t("Application")] as string);
        });
        return applications;
    };

    let applications = getArrayOfApplications();

    React.useEffect(() => {
        if (moneticsInitialized.current) return;
        moneticsInitialized.current = true;

        const source =
            validSelectedRows.length === 1 ? validSelectedRows[0] : rowData;

        source?.subRows?.forEach((el: SubRow) => {
            if (el.peripheralType === PERIPHERAL_TYPE_MONETIC) {
                const alreadyExists = associateStore.monetics.some(
                    (m: PeripheralElement) => m.Id === el.id_monetic
                );
                if (!alreadyExists) {
                    setMonetics({ ...el, Id: el.id_monetic });
                }
            }
        });
        // eslint-disable-next-line
    }, [rowData]);

    React.useEffect(() => {
        UpdateResourceIp(userID, franchiseID, fetchGlobal);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        setActions(confirmNavigation);
        setIsActionsClicked(showPrompt as boolean);
        setValidationAction(cancelNavigation);
    }, [showPrompt, confirmNavigation, cancelNavigation]);

    return {
        t,
        getAuthHeader,
        oneShop,
        userID,
        franchiseID,
        shopID,
        isAddPeripheralClicked,
        setIsAddPeripheralClicked,
        isActionsClicked,
        actions,
        validationAction,
        isEdited,
        setIsEdited,
        shopId,
        modify,
        isAddNewMoneticsButtonClicked,
        setIsAddNewMoneticsButtonClicked,
        isModalOpened,
        setIsModalOpened,
        data,
        setData,
        isConfirmationEditRunning,
        setIsConfirmationEditRunning,
        isInInitializationModalOpened,
        setIsInInitializationModalOpened,
        i18nextLng,
        editedData,
        setEditedData,
        moneticId,
        dataMoneticIsUpdated,
        setDataMoneticIsUpdated,
        filterActive,
        setLocalFilterActive,
        isModalDeleteAssociatePeripheralOpened,
        setIsModalDeleteAssociatePeripheralOpened,
        selectMark,
        setSelectMark,
        inputDesignation,
        setInputDesignation,
        InputIp,
        setInputIp,
        ipExist,
        setIpExist,
        setIsUpdateOperation,
        arrayOfCustomColumns,
        customJsxComponent,
        applications,
        setIsActionsClicked,
    };
}
